import React, { useMemo, useState, useCallback, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { usePopper } from 'react-popper';
import { Spinner } from 'flowbite-react';
import {
  FiCalendar, FiList, FiClock, FiChevronLeft, FiChevronDown,
  FiAlertTriangle, FiPlus, FiEdit2, FiTrash2, FiSearch,
} from 'react-icons/fi';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';
import { WorkspaceContext, useWorkspace } from '../../context/WorkspaceContext';
import { StandaloneAllocationModal } from '../planning/StandaloneAllocationModal';

interface PersonalWorkboardProps {
  personId: string;
  dependencyGraph: wasm.WrappedDependencyGraph | null;
}

function fmtEffort(tenths: number): string {
  if (tenths === 0) return '0';
  const days = tenths / 10;
  return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function epochDates(epoch: any): { start: string; end: string } {
  if (epoch?.type === 'Day') return { start: epoch.day, end: epoch.day };
  if (epoch?.type === 'Days') return { start: epoch.start_inclusive, end: epoch.end_inclusive };
  return { start: '', end: '' };
}

const PAGE_SIZE = 10;
const QUEUE_PAGE_SIZE = 20;
const SEARCH_LIMIT = 5;

/**
 * Two-column workboard: committed allocations (left) and work queue (right).
 */
const PersonalWorkboard: React.FC<PersonalWorkboardProps> = ({ personId, dependencyGraph }) => {
  const { database, applyCommand } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const { buildUrl, workspaceId } = useWorkspace();
  const { schedule: fuzzySchedule } = useSchedule();

  const [showPast, setShowPast] = useState(false);
  const [pastPage, setPastPage] = useState(0);
  const [queueExpanded, setQueueExpanded] = useState(false);
  const [assignModal, setAssignModal] = useState<{ entityId: string; label: string } | null>(null);
  const [editModal, setEditModal] = useState<{ entityId: string; label: string } | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  // ─── Left column: manual allocations for this person ──────────────
  const { currentAllocations, pastAllocations } = useMemo(() => {
    if (!database) return { currentAllocations: [], pastAllocations: [] };

    const raw: any[] = database.manual_allocations_for_person?.(personId) ?? [];
    const tasks = database.tasks();

    const enriched = raw.map((pa: any) => {
      const alloc = pa.allocation;
      const { start, end } = epochDates(alloc.when);
      const entityId = pa.entity;
      const task = tasks[entityId];
      return {
        id: alloc.id,
        entityId,
        label: task?.title?.() ?? entityId,
        effort: alloc.time ?? 0,
        startDate: start,
        endDate: end,
        complete: task?.complete?.() ?? false,
      };
    });

    const current = enriched
      .filter(a => a.endDate >= today)
      .sort((a, b) => a.startDate.localeCompare(b.startDate));

    const past = enriched
      .filter(a => a.endDate < today)
      .sort((a, b) => b.startDate.localeCompare(a.startDate));

    return { currentAllocations: current, pastAllocations: past };
  }, [database, personId, today]);

  const visiblePast = useMemo(() => {
    const end = (pastPage + 1) * PAGE_SIZE;
    return pastAllocations.slice(0, end);
  }, [pastAllocations, pastPage]);

  const handleDelete = useCallback(async (allocationId: string) => {
    await applyCommand({
      type: 'DeleteTaskResourceAllocation',
      id: allocationId,
    } as wasm.DatabaseCommand);
  }, [applyCommand]);

  // ─── Task search ──────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const [searchPopperElement, setSearchPopperElement] = useState<HTMLDivElement | null>(null);

  const { styles: searchPopperStyles, attributes: searchPopperAttrs, update: searchPopperUpdate } = usePopper(
    searchInputRef.current, searchPopperElement,
    { placement: 'bottom-start', modifiers: [{ name: 'offset', options: { offset: [0, 5] } }] },
  );

  useEffect(() => {
    if (showSearchDropdown && searchPopperUpdate) searchPopperUpdate();
  }, [showSearchDropdown, searchPopperUpdate]);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchDropdownRef.current && !searchDropdownRef.current.contains(e.target as Node) &&
        searchInputRef.current && !searchInputRef.current.contains(e.target as Node)
      ) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchTerm || !safeData) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const results = await wasm.search_dependencies(safeData, searchTerm.toLowerCase(), SEARCH_LIMIT);
        const formatted = results.map((result: any) => {
          try {
            const dep = result.dependency();
            const project = result.project();
            const task = dep.as_task();
            const outcome = dep.as_outcome();
            if (task) {
              return {
                entityId: task.id(),
                title: task.title(),
                description: task.description?.() ?? null,
                projectName: project?.name?.() ?? null,
                type: 'task' as const,
              };
            } else if (outcome) {
              return {
                entityId: outcome.id(),
                title: outcome.title(),
                description: outcome.description?.() ?? null,
                projectName: project?.name?.() ?? null,
                type: 'outcome' as const,
              };
            }
            return null;
          } catch { return null; }
        }).filter(Boolean);

        // Enrich with blocker info
        if (dependencyGraph && database && formatted.length > 0) {
          const taskNodeIds = formatted
            .filter((r: any) => r.type === 'task')
            .map((r: any) => ({ type: 'Task', id: r.entityId }));
          if (taskNodeIds.length > 0) {
            try {
              const blockers = dependencyGraph.incomplete_blockers_for_nodes(
                database as unknown as wasm.WrappedDatabase,
                taskNodeIds as unknown as wasm.DependencyNodeId[],
              ) ?? {};
              for (const r of formatted) {
                const key = `Task:${(r as any).entityId}`;
                (r as any).blockers = blockers[key] ?? [];
              }
            } catch { /* ignore */ }
          }
        }

        setSearchResults(formatted);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, safeData, dependencyGraph, database]);

  const handleSearchSelect = useCallback((entityId: string, label: string) => {
    setSearchTerm('');
    setShowSearchDropdown(false);
    setAssignModal({ entityId, label });
  }, []);

  // ─── Right column: work queue from fuzzy schedule ─────────────────
  const { queueItems, blockerMap } = useMemo(() => {
    if (!fuzzySchedule || !database) return { queueItems: [], blockerMap: {} as Record<string, any[]> };

    const sched = fuzzySchedule as any;
    const meta = sched.meta;
    const stats = sched.stats ?? {};
    const items = sched.schedule?.items ?? {};
    const nodeToEntity = meta?.node_to_entity ?? {};
    const resourceToPerson = meta?.resource_to_person ?? {};

    const personResourceIds = new Set<string>();
    for (const [rid, pid] of Object.entries(resourceToPerson)) {
      if (pid === personId) personResourceIds.add(rid);
    }

    const personItems: {
      nodeId: string;
      entityId: string;
      entityType: string;
      label: string;
      p50Start: number;
      p50StartDate: string | null;
      p50EndDate: string | null;
    }[] = [];

    const nodeLabels = meta?.node_labels ?? {};
    const tasks = database.tasks();

    for (const [nodeId, item] of Object.entries(items)) {
      const contributions = (item as any).resource_contributions ?? {};
      const rids = Object.keys(contributions);
      if (!rids.some(rid => personResourceIds.has(rid))) continue;

      const entity = nodeToEntity[nodeId];
      if (!entity) continue;

      const entityType = entity.type ?? '';
      const entityId = entity.id ?? '';
      const label = nodeLabels[nodeId] ?? tasks[entityId]?.title?.() ?? entityId;

      const nodeStat = stats[nodeId];
      const p50Start = nodeStat?.start?.p50 ?? 0;
      const p50StartDate = nodeStat?.start?.p50_date ?? null;
      const p50EndDate = nodeStat?.finish?.p50_date ?? null;

      if (entityType === 'Task' && tasks[entityId]?.complete?.()) continue;

      personItems.push({ nodeId, entityId, entityType, label, p50Start, p50StartDate, p50EndDate });
    }

    personItems.sort((a, b) => a.p50Start - b.p50Start);

    const allocatedEntityIds = new Set(currentAllocations.map(a => a.entityId));
    const filtered = personItems.filter(item => !allocatedEntityIds.has(item.entityId));

    let blockers: Record<string, any[]> = {};
    if (dependencyGraph && database && filtered.length > 0) {
      const nodeIds = filtered.map(item => {
        if (item.entityType === 'Task') {
          return { type: 'Task', id: item.entityId };
        } else if (item.entityType === 'WorkflowStep') {
          return { type: 'WorkflowTemplateStep', instance_id: item.entityId, step_id: '' };
        }
        return { type: item.entityType, id: item.entityId };
      }).filter(n => n.type === 'Task' || n.type === 'Outcome');

      try {
        blockers = dependencyGraph.incomplete_blockers_for_nodes(
          database as unknown as wasm.WrappedDatabase,
          nodeIds as unknown as wasm.DependencyNodeId[],
        ) ?? {};
      } catch (e) {
        console.warn('Failed to compute blockers:', e);
      }
    }

    return { queueItems: filtered, blockerMap: blockers };
  }, [fuzzySchedule, database, personId, currentAllocations, dependencyGraph]);

  const visibleQueue = queueExpanded ? queueItems : queueItems.slice(0, QUEUE_PAGE_SIZE);

  // ─── Priority delimiters ──────────────────────────────────────────
  const priorityDelimiters = useMemo(() => {
    if (!database) return new Map<number, { name: string; ownerId: string; ownerType: string }>();

    const owners: any[] = database.priority_item_owners?.() ?? [];
    const entityToPriority = new Map<string, { name: string; ownerId: string; ownerType: string; position: string }>();

    for (const owner of owners) {
      const ownerType = owner.type_ ?? owner.type ?? '';
      const ownerId = owner.id ?? '';
      const items: any[] = database.priority_items_for_owner?.(ownerType, ownerId) ?? [];
      const ownerName = ownerType === 'Team'
        ? (database.teams()[ownerId]?.name?.() ?? ownerId)
        : (database.people()[ownerId]?.name?.() ?? ownerId);

      for (const item of items) {
        const eid = item.entity_id?.id;
        if (eid) {
          entityToPriority.set(eid, {
            name: `${ownerName} priorities`,
            ownerId,
            ownerType,
            position: item.position,
          });
        }
      }
    }

    const delimiters = new Map<number, { name: string; ownerId: string; ownerType: string }>();
    let lastPriority: string | null = null;
    for (let i = 0; i < visibleQueue.length; i++) {
      const prio = entityToPriority.get(visibleQueue[i].entityId);
      const prioKey = prio ? `${prio.ownerType}:${prio.ownerId}` : '__unprioritized__';
      if (prioKey !== lastPriority) {
        if (prio) {
          delimiters.set(i, { name: prio.name, ownerId: prio.ownerId, ownerType: prio.ownerType });
        } else if (lastPriority !== null) {
          delimiters.set(i, { name: 'Unprioritized', ownerId: '', ownerType: '' });
        }
        lastPriority = prioKey;
      }
    }

    return delimiters;
  }, [database, visibleQueue]);

  const handleAssign = useCallback((entityId: string, label: string) => {
    setAssignModal({ entityId, label });
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ── Left: My Commitments ───────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="w-4 h-4 text-cm-accent-blue" />
          <h3 className="font-mono font-medium text-cm-text-primary">My Commitments</h3>
          <span className="text-xs text-cm-text-muted ml-auto">{currentAllocations.length} active</span>
        </div>

        {/* Task search */}
        <div className="mb-4 relative">
          <div className="relative">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              className="w-full text-xs bg-cm-bg-deep border border-cm-border-subtle rounded-lg pl-8 pr-3 py-2 text-cm-text-primary placeholder:text-cm-text-muted focus:ring-1 focus:ring-cm-accent-blue focus:border-cm-accent-blue"
              placeholder="Search tasks to schedule..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setShowSearchDropdown(true);
                if (e.target.value) { setIsSearching(true); setHasSearched(false); }
              }}
              onFocus={() => { if (searchTerm) setShowSearchDropdown(true); }}
            />
          </div>
          {showSearchDropdown && searchTerm && createPortal(
            <div
              ref={el => { searchDropdownRef.current = el; setSearchPopperElement(el); }}
              className="z-50 bg-cm-bg-surface rounded-lg border border-cm-border-subtle max-h-60 overflow-auto shadow-xl"
              style={{ ...searchPopperStyles.popper, width: searchInputRef.current?.offsetWidth }}
              {...searchPopperAttrs.popper}
            >
              {isSearching ? (
                <div className="px-4 py-3 text-cm-text-muted flex items-center justify-center gap-2 text-xs">
                  <Spinner size="sm" /> Searching...
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((r: any) => {
                  const blockers: any[] = r.blockers ?? [];
                  const isBlocked = blockers.length > 0;
                  return (
                    <button
                      key={r.entityId}
                      onClick={() => handleSearchSelect(r.entityId, r.title)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-cm-bg-hover transition-colors ${
                        isBlocked ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-cm-text-primary truncate">{r.title}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                          r.type === 'task'
                            ? 'bg-blue-900/40 text-blue-400'
                            : 'bg-green-900/40 text-green-400'
                        }`}>
                          {r.type === 'task' ? 'Task' : 'Outcome'}
                        </span>
                        {isBlocked && (
                          <span title={`Blocked by: ${blockers.map((b: any) => b.title).join(', ')}`}>
                            <FiAlertTriangle className="w-3 h-3 text-amber-500/70 shrink-0" />
                          </span>
                        )}
                      </div>
                      {r.projectName && (
                        <span className="text-[10px] text-cm-text-muted">{r.projectName}</span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-cm-text-muted text-xs">
                  {hasSearched ? 'No matching tasks found' : 'Type to search...'}
                </div>
              )}
            </div>,
            document.body,
          )}
        </div>

        {currentAllocations.length === 0 ? (
          <div className="flex items-center justify-center h-24 border border-dashed border-cm-border-mid rounded-lg">
            <p className="text-cm-text-muted text-sm font-mono">No active commitments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {currentAllocations.map((alloc) => (
              <div
                key={alloc.id}
                className="group rounded-lg border border-cm-border-subtle p-3 hover:border-cm-border-mid transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to={buildUrl({ kind: 'task', taskId: alloc.entityId })}
                    className="text-sm font-medium text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
                  >
                    {alloc.label}
                  </Link>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs font-mono text-cm-text-muted">
                      {fmtEffort(alloc.effort)}
                    </span>
                    <button
                      onClick={() => setEditModal({ entityId: alloc.entityId, label: alloc.label })}
                      className="p-1 rounded text-cm-text-muted opacity-0 group-hover:opacity-100 hover:text-cm-accent-blue hover:bg-cm-accent-blue/10 transition-all"
                      title="Edit allocation"
                    >
                      <FiEdit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(alloc.id)}
                      className="p-1 rounded text-cm-text-muted opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-400/10 transition-all"
                      title="Remove allocation"
                    >
                      <FiTrash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-[11px] text-cm-text-muted">
                  <FiClock className="w-3 h-3" />
                  <span>
                    {fmtDate(alloc.startDate)}
                    {alloc.startDate !== alloc.endDate && ` – ${fmtDate(alloc.endDate)}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show past work */}
        {pastAllocations.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowPast(!showPast)}
              className="flex items-center gap-1.5 text-xs text-cm-text-muted hover:text-cm-text-primary transition-colors"
            >
              <FiChevronLeft className={`w-3 h-3 transition-transform ${showPast ? 'rotate-[-90deg]' : ''}`} />
              {showPast ? 'Hide' : 'Show'} past work ({pastAllocations.length})
            </button>
            {showPast && (
              <div className="mt-2 space-y-2">
                {visiblePast.map((alloc) => (
                  <div
                    key={alloc.id}
                    className="group rounded-lg border border-cm-border-subtle/50 p-3 opacity-60 hover:opacity-80 transition-opacity"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to={buildUrl({ kind: 'task', taskId: alloc.entityId })}
                        className="text-sm text-cm-text-secondary hover:text-cm-accent-orange transition-colors truncate"
                      >
                        {alloc.label}
                      </Link>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs font-mono text-cm-text-muted">
                          {fmtEffort(alloc.effort)}
                        </span>
                        <button
                          onClick={() => handleDelete(alloc.id)}
                          className="p-1 rounded text-cm-text-muted opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-400/10 transition-all"
                          title="Remove allocation"
                        >
                          <FiTrash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-[11px] text-cm-text-muted mt-1">
                      {fmtDate(alloc.startDate)}
                      {alloc.startDate !== alloc.endDate && ` – ${fmtDate(alloc.endDate)}`}
                    </div>
                  </div>
                ))}
                {visiblePast.length < pastAllocations.length && (
                  <button
                    onClick={() => setPastPage(p => p + 1)}
                    className="w-full py-2 text-xs text-cm-text-muted hover:text-cm-text-primary transition-colors border border-dashed border-cm-border-mid rounded-lg"
                  >
                    Load more ({pastAllocations.length - visiblePast.length} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Right: Work Queue ──────────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <FiList className="w-4 h-4 text-cm-accent-purple" />
          <h3 className="font-mono font-medium text-cm-text-primary">Work Queue</h3>
          <span className="text-xs text-cm-text-muted ml-auto">
            {queueItems.length} item{queueItems.length !== 1 ? 's' : ''}
          </span>
        </div>

        {queueItems.length === 0 ? (
          <div className="flex items-center justify-center h-24 border border-dashed border-cm-border-mid rounded-lg">
            <p className="text-cm-text-muted text-sm font-mono">No queued work</p>
          </div>
        ) : (
          <div className="space-y-1">
            {visibleQueue.map((item, idx) => {
              const delimiter = priorityDelimiters.get(idx);
              const blockers: any[] = blockerMap[`Task:${item.entityId}`] ?? [];
              const isBlocked = blockers.length > 0;

              return (
                <React.Fragment key={item.nodeId}>
                  {delimiter && (
                    <div className="flex items-center gap-2 py-2 mt-1">
                      <div className="h-px flex-1 bg-cm-border-subtle" />
                      {delimiter.ownerId ? (
                        <Link
                          to={buildUrl({ kind: 'team', teamId: delimiter.ownerId })}
                          className="text-[10px] font-mono uppercase tracking-wider text-cm-accent-purple hover:text-cm-accent-purple/80 transition-colors"
                        >
                          {delimiter.name}
                        </Link>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-cm-text-muted">
                          {delimiter.name}
                        </span>
                      )}
                      <div className="h-px flex-1 bg-cm-border-subtle" />
                    </div>
                  )}
                  <div
                    className={`group flex items-center gap-2 rounded-lg border p-3 transition-colors ${
                      isBlocked
                        ? 'border-cm-border-subtle/50 opacity-60'
                        : 'border-cm-border-subtle hover:border-cm-border-mid'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link
                          to={buildUrl({ kind: 'task', taskId: item.entityId })}
                          className="text-sm font-medium text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
                        >
                          {item.label}
                        </Link>
                        {isBlocked && (
                          <span
                            className="shrink-0"
                            title={`Blocked by: ${blockers.map((b: any) => b.title).join(', ')}`}
                          >
                            <FiAlertTriangle className="w-3.5 h-3.5 text-amber-500/70" />
                          </span>
                        )}
                      </div>
                      {item.p50StartDate && (
                        <span className="text-[11px] text-cm-text-muted">
                          ~{fmtDate(item.p50StartDate)}
                          {item.p50EndDate && ` – ${fmtDate(item.p50EndDate)}`}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAssign(item.entityId, item.label)}
                      className="shrink-0 p-1.5 rounded-md text-cm-text-muted opacity-0 group-hover:opacity-100 hover:text-cm-accent-blue hover:bg-cm-accent-blue/10 transition-all"
                      title="Schedule this work"
                    >
                      <FiPlus className="w-4 h-4" />
                    </button>
                  </div>
                </React.Fragment>
              );
            })}

            {!queueExpanded && queueItems.length > QUEUE_PAGE_SIZE && (
              <button
                onClick={() => setQueueExpanded(true)}
                className="w-full py-2 mt-2 text-xs text-cm-text-muted hover:text-cm-text-primary transition-colors border border-dashed border-cm-border-mid rounded-lg flex items-center justify-center gap-1.5"
              >
                <FiChevronDown className="w-3 h-3" />
                Show more ({queueItems.length - QUEUE_PAGE_SIZE} remaining)
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}
      {assignModal && (
        <StandaloneAllocationModal
          entityId={assignModal.entityId}
          entityLabel={assignModal.label}
          personId={personId}
          onClose={() => setAssignModal(null)}
        />
      )}
      {editModal && (
        <StandaloneAllocationModal
          entityId={editModal.entityId}
          entityLabel={editModal.label}
          personId={personId}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
};

export default PersonalWorkboard;
